import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography
} from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../../../app/hooks';
import { closeAddNoteDialog } from '../../../state';
import { useNotesManagement } from '../../../hooks/useNotesManagement';

export const AddNoteDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showAddNoteDialog } = useAppSelector(state => state.ticket.ui);
  const { addNote } = useNotesManagement();
  
  const [noteText, setNoteText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setNoteText('');
    dispatch(closeAddNoteDialog());
  };

  const handleSubmit = async () => {
    if (!noteText.trim()) return;
    
    setIsSubmitting(true);
    try {
      addNote(noteText.trim());
      handleClose();
    } catch (error) {
      console.error('Error adding note:', error);
      // TODO: Add proper error handling/toast notification
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={showAddNoteDialog}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: 600
      }}>
        Add New Note
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 2 }}>
            Add a note to document important information about this ticket.
          </Typography>
          
          <TextField
            autoFocus
            multiline
            rows={6}
            fullWidth
            placeholder="Enter your note here..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="outlined"
            InputProps={{
              sx: {
                alignItems: 'flex-start',
                '& .MuiInputBase-input': {
                  paddingTop: '8px',
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
              }
            }}
          />
          
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Tip: Press Ctrl+Enter to save quickly
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 2.5, 
        borderTop: '1px solid #e2e8f0',
        backgroundColor: '#f8fafc'
      }}>
        <Button 
          onClick={handleClose}
          color="inherit"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!noteText.trim() || isSubmitting}
          sx={{
            backgroundColor: '#162346',
            '&:hover': { backgroundColor: '#1a2a52' },
            fontWeight: 600
          }}
        >
          {isSubmitting ? 'Adding...' : 'Add Note'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
